import { createContext, useContext, useState, useCallback, useEffect } from 'react';

const CartContext = createContext(null);

export function CartProvider({ children }) {
  const [items, setItems] = useState(() => {
    try { const saved = localStorage.getItem('cart_items'); return saved ? JSON.parse(saved) : []; } catch { return []; }
  });
  const [orderType, setOrderType] = useState('takeaway');
  const [tableInfo, setTableInfo] = useState(null);

  useEffect(() => { localStorage.setItem('cart_items', JSON.stringify(items)); }, [items]);

  const addItem = useCallback((dish, quantity = 1, selectedSupplements = [], priceKey = 'delivery', extra = {}) => {
    const price = extra.priceOverride ?? dish.prices?.[priceKey] ?? dish.prices?.dine_in ?? 0;
    const normalizedItem = {
      dish_id: dish.id,
      name: extra.displayName || dish.name,
      price,
      quantity,
      supplements: selectedSupplements,
      image: dish.image,
      formula_selections: extra.formulaSelections || [],
      base_name: dish.name,
      vat_rate: extra.vatRate ?? dish.vat_rate ?? null,
      surcharge_total: extra.surchargeTotal ?? 0,
      offer_reward_meta: extra.offerRewardMeta || null,
    };
    setItems(prev => {
      const existing = prev.find(i =>
        i.dish_id === normalizedItem.dish_id &&
        JSON.stringify(i.supplements) === JSON.stringify(selectedSupplements) &&
        JSON.stringify(i.formula_selections || []) === JSON.stringify(normalizedItem.formula_selections || []) &&
        i.name === normalizedItem.name &&
        JSON.stringify(i.offer_reward_meta || null) === JSON.stringify(normalizedItem.offer_reward_meta || null)
      );
      if (existing) {
        return prev.map(i => i === existing ? { ...i, quantity: i.quantity + quantity } : i);
      }
      return [...prev, normalizedItem];
    });
  }, []);

  const removeItem = useCallback((index) => {
    setItems(prev => prev.filter((_, i) => i !== index));
  }, []);

  const updateQuantity = useCallback((index, quantity) => {
    if (quantity <= 0) {
      setItems(prev => prev.filter((_, i) => i !== index));
      return;
    }
    setItems(prev => prev.map((item, i) => i === index ? { ...item, quantity } : item));
  }, []);

  const clearCart = useCallback(() => setItems([]), []);

  const total = items.reduce((sum, item) => sum + item.price * item.quantity, 0);
  const itemCount = items.reduce((sum, item) => sum + item.quantity, 0);

  return (
    <CartContext.Provider value={{
      items, orderType, setOrderType, tableInfo, setTableInfo,
      addItem, removeItem, updateQuantity, clearCart, total, itemCount
    }}>
      {children}
    </CartContext.Provider>
  );
}

export function useCart() {
  const ctx = useContext(CartContext);
  if (!ctx) throw new Error('useCart must be within CartProvider');
  return ctx;
}
